import React, { CSSProperties } from 'react';
import defaultLocales from 'globals/locales/default';
import { LoadingState, TransactionType, UserRole } from './types';

export const nextTick = (ms = 0): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const isLastElement = (idx: number, arrayLength: number): boolean => {
  return idx === arrayLength - 1;
};

export const getId = (): string => {
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the first 9 characters
  // after the decimal.
  return `_${Math.random().toString(36).substr(2, 9)}`;
};

export const getNumberId = (): number => {
  return new Date().getUTCMilliseconds() + Math.random();
};

export const generateArrayOfNumbers = (length: number): number[] => {
  return Array.from(Array(length).keys());
};

export const generateDate = (numberOfDays: number): number => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.setDate(date.getDate() + numberOfDays);
};

export const getNumberOfDigits = (n: number): number => {
  return n?.toString().length;
};

export const normalizeTimestamp = (timestamp: number): number => {
  return getNumberOfDigits(timestamp) < 12 ? timestamp * 1000 : timestamp;
};

export const formatDate = (timestamp: number, withYear = false): string => {
  const date =
    getNumberOfDigits(timestamp) < 12
      ? new Date(timestamp * 1000)
      : new Date(timestamp);

  const options = {
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined,
  };

  return date.toLocaleDateString('en-GB', options);
};

export const getDayName = (timestamp: number): string => {
  const date = new Date(normalizeTimestamp(timestamp));

  const options = {
    weekday: 'long',
  };

  return date.toLocaleDateString('en-GB', options);
};

export const formatTime = (timestamp: number): string => {
  const date =
    getNumberOfDigits(timestamp) < 12
      ? new Date(timestamp * 1000)
      : new Date(timestamp);

  const hours = `0${date.getHours()}`.slice(-2); // Get the hour (0-23)
  const minutes = `0${date.getMinutes()}`.slice(-2); // Get the minutes (0-59)
  return `${hours}:${minutes}`;
};

export const getDateDiffInDays = (start: number, end: number): number => {
  // Take the difference between the dates and divide by milliseconds per day.
  // Round to nearest whole number to deal with DST.
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
};

export const randomInteger = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const isInitialState = (state: LoadingState): boolean =>
  state === LoadingState.INITIAL;

export const isLoadingState = (state: LoadingState): boolean =>
  state === LoadingState.LOADING;

export const isSuccessState = (state: LoadingState): boolean =>
  state === LoadingState.SUCCESS;

export const isFailedState = (state: LoadingState): boolean =>
  state === LoadingState.FAILED;

export const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getCombinedStatus = (
  statusArray: LoadingState[]
): LoadingState => {
  if (statusArray.includes(LoadingState.FAILED)) return LoadingState.FAILED;
  if (statusArray.includes(LoadingState.LOADING)) return LoadingState.LOADING;
  if (statusArray.every((s) => s === LoadingState.SUCCESS))
    return LoadingState.SUCCESS;
  if (statusArray.every((s) => s === LoadingState.INITIAL))
    return LoadingState.INITIAL;
  return LoadingState.FAILED;
};

export const getCurrency = ({
  currency,
  format,
  value,
  options,
}: {
  value: number;
  currency?: string;
  format?: string;
  options?: Intl.NumberFormatOptions;
}): string => {
  return new Intl.NumberFormat(format || 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    ...options,
  }).format(value);
};

export const getCurrencySymbol = ({
  currency,
  format,
  options,
}: {
  value: number;
  currency?: string;
  format?: string;
  options?: Intl.NumberFormatOptions;
}): string | undefined => {
  return new Intl.NumberFormat(format || 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    ...options,
  })
    .formatToParts()
    .find(({ type }) => type === 'currency')?.value;
};

export const getDate = ({
  format,
  value,
  options = {},
}: {
  value: number;
  format?: string;
  options?: Intl.DateTimeFormatOptions;
}): string => {
  const date = new Date(normalizeTimestamp(value));
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return new Intl.DateTimeFormat(format || 'en-US', {
    ...defaultOptions,
    ...options,
  }).format(date);
};

export const convertToSlug = (string: string): string => {
  return string
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/_/g, ' ')
    .replace(/ +/g, '-');
};

export const getPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const flatten = (arr: any[]): any[] => {
  return [].concat(...arr);
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/no-explicit-any
export const deepFlatten = (arr: any[]): any[] => {
  return flatten(
    arr.map(
      (
        x // with each x in array
      ) =>
        Array.isArray(x) // is x an array?
          ? deepFlatten(x) // if yes, return deeply flattened x
          : x // if no, return just x
    )
  );
};

export const isCorrectDate = (d: string | number | Date): boolean => {
  if (Object.prototype.toString.call(d) === '[object Date]') {
    // it is a date
    if (Number.isNaN((d as Date).getTime())) {
      // d.valueOf() could also work
      return false;
    }
    // date is valid
    return true;
  }
  return false;
  // not a date
};

export const getIconSrcFromSrcList = (
  iconSrcList: string[],
  iconFileName: string,
  fallbackIconSrc: string
): string => {
  const iconSrcFromList = iconSrcList.find((iconPath) => {
    return iconPath.includes(iconFileName);
  });
  if (iconSrcFromList) return iconSrcFromList;
  return fallbackIconSrc;
};

export const setCookie = (
  name: string,
  value: never | string,
  days: number
): void => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const eraseCookie = (name: string): void => {
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

export const sentenceCase = (s: string): string => {
  const result = s
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase();
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const slugify = (s?: string): string | null => {
  if (!s) return null;
  return s
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/_/g, '-')
    .replace(/[^\w-]+/g, '');
};

export const revertSlugify = (s?: string): string | null => {
  if (!s) return null;
  return s.toUpperCase().replace(/-/g, '_');
};

export const lowerCase = (s: string): string => {
  return (
    s
      // .replace(/([A-Z])/g, ' $1')
      // .trim()
      .toLowerCase()
  );
};

const monthDiff = (timestampInPast: number): number => {
  const dateInPast = new Date(normalizeTimestamp(timestampInPast));
  const dateNow = new Date();
  let months;
  months = (dateNow.getFullYear() - dateInPast.getFullYear()) * 12;
  months -= dateInPast.getMonth();
  months += dateNow.getMonth();
  return months <= 0 ? 0 : months;
};

export const getReadableTimeDiff = (timestampInPast: number): string => {
  const dateInPast = new Date(normalizeTimestamp(timestampInPast)).getTime();
  const dateNow = new Date().getTime();

  let diffInMilliSeconds = Math.abs(dateNow - dateInPast) / 1000;

  // calculate months
  const months = monthDiff(timestampInPast);
  const years = Math.floor(months / 12);

  // calculate days
  const days = Math.floor(diffInMilliSeconds / 86400);
  diffInMilliSeconds -= days * 86400;

  // calculate hours
  const hours = Math.floor(diffInMilliSeconds / 3600) % 24;
  diffInMilliSeconds -= hours * 3600;

  // calculate minutes
  const minutes = Math.floor(diffInMilliSeconds / 60) % 60;

  switch (true) {
    case years > 2:
      return `${years} years ago`;
    case years === 1:
      return `${years} year ago`;
    case months > 2:
      return `${months} months ago`;
    case months === 1:
      return `${months} months ago`;
    case days > 2:
      return `${days} days ago`;
    case days === 1:
      return `${days} day ago`;
    case hours > 2:
      return `${hours} hours ago`;
    case hours === 1:
      return `${hours} hour ago`;
    case minutes > 1:
      return `${minutes} minutes ago`;
    default:
      return 'few seconds ago';
  }
};

interface GetMonthOptionsReturn {
  value: number;
  label: string;
}

export const getMonthOptions = (
  type: 'long' | 'short' = 'long'
): GetMonthOptionsReturn[] =>
  Array.from({ length: 12 }, (e, i) => ({
    label: new Date(-1, i).toLocaleDateString('en', {
      month: type,
    }),
    value: i + 1,
  }));

interface GetFutureYearOptionsReturn {
  value: number;
}

export const getFutureYearOptions = (
  length = 5
): GetFutureYearOptionsReturn[] =>
  Array.from(Array(length).keys()).map((i) => ({
    value: new Date().getFullYear() + i,
  }));

interface TransactionTypeOptionProps {
  value: string;
  label: string;
}

export const getTransactionTypeOptions = (): TransactionTypeOptionProps[] => {
  return [
    { value: 'all', label: 'All' },
    {
      value: TransactionType.INCOME,
      label: defaultLocales.enums[TransactionType.INCOME],
    },
    {
      value: TransactionType.EXPENDITURE,
      label: defaultLocales.enums[TransactionType.EXPENDITURE],
    },
  ];
};

export const isContainsOnlyLetters = (str: string): boolean => {
  return /^[a-zA-Z]+$/.test(str);
};

export const formatCcNumber = (value: string): string => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  if (parts.length) {
    return parts.join(' ');
  }

  return v;
};

export const postalCodeIsValidRe = /^[A-Za-z0-9]([A-Za-z0-9- ]{2,254})$/;
export const postalCodeIsPotentiallyValidRe = /^$|^[A-Za-z0-9]([A-Za-z0-9- ]*)$/;

export const formatPostalCode = (
  newValue: string,
  oldValue: string
): string => {
  if (newValue.match(postalCodeIsPotentiallyValidRe)) return newValue;
  return oldValue;
};

export const removeSpaces = (str: string): string => {
  return str.replace(/ /g, '');
};

export const emailIsValidRe = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

export const preventLosingCaretPosition = (
  e: React.ChangeEvent<HTMLInputElement>
): void => {
  const element = e.target;
  const caret = element.selectionStart;
  window.requestAnimationFrame(() => {
    element.selectionStart = caret;
    element.selectionEnd = caret;
  });
};

export const centerBoxProps = {
  display: 'flex',
  alignItems: `center`,
  justifyContent: `center`,
};

export const spaceBetweenBoxProps = {
  ...centerBoxProps,
  justifyContent: `space-between`,
};

export const ellipsisProps: CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const isUserRole = (
  userRole: UserRole | undefined,
  options: UserRole | UserRole[]
): boolean => {
  return userRole ? options.includes(userRole) : false;
};

interface SingleUserRoleListItem {
  value: UserRole;
}

export const getUserRoleList = (): SingleUserRoleListItem[] => [
  { value: UserRole.ADMIN },
  { value: UserRole.LEADER },
  { value: UserRole.ACCOUNTANT },
  { value: UserRole.USER },
  { value: UserRole.DEV },
];

export const matchExact = (r: RegExp, str: string): boolean => {
  const match = str.match(r);
  return !!(match && str === match[0]);
};

const fsCookieVerified = {
  key: 'fs-verified',
  value: 'true',
};

export const setSessionVerified = (): void => {
  const { key, value } = fsCookieVerified;
  sessionStorage.setItem(key, value);
};

export const delSessionVerified = (): void => {
  const { key } = fsCookieVerified;
  sessionStorage.removeItem(key);
};

export const getSessionVerified = (): boolean => {
  const { key, value } = fsCookieVerified;
  return sessionStorage.getItem(key) === value;
};

export const checkIfFulfillRequired = (
  values: Array<string | boolean>
): boolean => {
  return values.every(
    (value) => value === true || value === false || value !== ''
  );
};
